import { Tooltip, Chip, Divider, Stack } from '@mui/material';
import { Fragment } from 'react';

import { Tag } from '@mui/icons-material';

import { grey } from '@mui/material/colors';

import { graphql, type DocumentType } from '@/gql';
import { extractConnectionProps } from '@/utils/queries/queryExtracts';
import { nonFalsey } from '@/utils/nonFalsey';
import { Or2Link } from '@/components/navigation/Or2Link';
import { env } from '@/env';

const POST_TAGS_BAR_POST_FRAGMENT = graphql(`
  fragment postTagsBarPostFragment on Post {
    _id
    hasPostLinkedObjectsConnection {
      edges {
        node {
          _id
          targetType
          target {
            ... on PostTag {
              _id
              displayName
            }
          }
        }
      }
    }
  }
`);

const POST_TAGS_BAR_GROUP_FRAGMENT = graphql(`
  fragment postTagsBarGroupFragment on Group {
    _id
    machineName
  }
`);

interface Props {
  post: DocumentType<typeof POST_TAGS_BAR_POST_FRAGMENT>;
  group: DocumentType<typeof POST_TAGS_BAR_GROUP_FRAGMENT>;
}

export const PostTagsBar = ({ post, group }: Props) => {
  const { hasPostLinkedObjectsConnection } = post;
  const { machineName } = group;

  const tags = extractConnectionProps(hasPostLinkedObjectsConnection)
    .map((tag) => {
      if (tag.target?.__typename === 'PostTag') {
        return {
          _id: tag._id,
          target: tag.target,
        };
      }

      return null;
    })
    .filter(nonFalsey);

  return (
    <>
      <Stack
        useFlexGap
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ padding: 1, flexWrap: 'wrap' }}
      >
        <Tooltip title="Post topics">
          <Tag sx={{ color: grey[500], width: 16, marginRight: 1 }} />
        </Tooltip>
        {tags.map((tag) => {
          const { _id: linkId, target } = tag;
          const { _id: tagId, displayName } = target ?? {};

          const link = new URL(
            `${env.NEXT_PUBLIC_APP_URL}/g/${machineName}/noticeboard/search`,
          );

          if (tagId) {
            link.searchParams.set('topics', tagId);
          }

          return (
            <Fragment key={tagId}>
              <Or2Link noMuiStyle href={link}>
                <Tooltip
                  title={`Filter by ${displayName} only`}
                  placement="top-start"
                >
                  <Chip
                    color="secondary"
                    size="small"
                    key={linkId}
                    label={displayName}
                  />
                </Tooltip>
              </Or2Link>
            </Fragment>
          );
        })}
      </Stack>
      <Divider />
    </>
  );
};
